import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CompanyShell } from "@/components/CompanyShell";
import { ShieldCheck, Loader2, CheckCircle2, AlertTriangle, Clock, XCircle, Settings2, Save, Plus, Trash2 } from "lucide-react";

const FILTERS = [
  { key: "all", label: "Všetky", ck: "all" },
  { key: "active", label: "Aktívne súhlasy", ck: "active" },
  { key: "expiring", label: "Expirujúce do 2 mesiacov", ck: "expiring" },
  { key: "expired", label: "Expirované", ck: "expired" },
  { key: "pending", label: "Čakajúce na súhlas", ck: "pending" },
  { key: "retained", label: "Zamietnutí – uchovať v DB", ck: "retained" },
  { key: "shredded", label: "Zamietnutí – skartované údaje", ck: "shredded" },
];

const GDPR_BADGE = {
  ACTIVE: { l: "Aktívny", cls: "bg-emerald-50 text-emerald-700 border-emerald-100", icon: CheckCircle2 },
  EXPIRING_SOON: { l: "Vyprší čoskoro", cls: "bg-amber-50 text-amber-700 border-amber-100", icon: AlertTriangle },
  EXPIRED: { l: "Expirovaný", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: Clock },
  REVOKED: { l: "Odvolaný", cls: "bg-red-50 text-red-600 border-red-100", icon: XCircle },
  PENDING: { l: "Čaká na súhlas", cls: "bg-blue-50 text-blue-700 border-blue-100", icon: Clock },
  NONE: { l: "Bez súhlasu", cls: "bg-slate-50 text-slate-500 border-slate-200", icon: XCircle },
};

const PIPELINE = {
  new: "Nový", in_process: "Na schválenie", interview: "Na pohovor", hired: "Prijatý",
  rejected_retain: "Talent Pool", rejected_shredded: "Zamietnutý",
  rejected_anonymize: "Zamietnutý",
};

export function GdprBadge({ status }) {
  const b = GDPR_BADGE[status] || GDPR_BADGE.NONE;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${b.cls}`} data-testid="gdpr-badge">
      <b.icon size={12} /> {b.l}
    </span>
  );
}

export default function GdprDashboard() {
  const nav = useNavigate();
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    setData(null);
    api.get(`/company/gdpr/candidates?filter=${filter}`).then((r) => setData(r.data)).catch(() => setData({ candidates: [], counts: {} }));
  }, [filter]);

  const fmt = (d) => d ? new Date(d).toLocaleDateString("sk-SK") : "—";
  const counts = data?.counts || {};

  return (
    <CompanyShell>
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-blue-700 flex items-center justify-center"><ShieldCheck className="text-white" size={22} /></div>
          <div>
            <h1 className="font-head text-2xl font-bold tracking-tight">GDPR / Súhlasy</h1>
            <p className="text-sm text-slate-500">Prehľad súhlasov na uchovanie kandidátov v databáze</p>
          </div>
        </div>
        <button data-testid="gdpr-settings-toggle" onClick={() => setShowSettings((s) => !s)}
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
          <Settings2 size={16} /> Nastavenia GDPR
        </button>
      </div>

      {showSettings && <GdprSettingsPanel />}

      <div className="flex flex-wrap gap-2 mb-6" data-testid="gdpr-filters">
        {FILTERS.map((f) => {
          const n = counts[f.ck];
          return (
            <button key={f.key} data-testid={`gdpr-filter-${f.key}`} onClick={() => setFilter(f.key)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${filter === f.key ? "bg-blue-700 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {f.label}{typeof n === "number" && <span className={`text-[11px] px-1.5 rounded-full ${filter === f.key ? "bg-white/25" : "bg-slate-100"}`}>{n}</span>}
            </button>
          );
        })}
      </div>

      {!data ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-700" /></div>
      ) : data.candidates.length === 0 ? (
        <div className="rounded-3xl bg-white border border-slate-200 p-10 text-center text-slate-400" data-testid="gdpr-empty">Žiadni kandidáti v tomto filtri.</div>
      ) : (
        <div className="rounded-3xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="gdpr-list">
          <div className="hidden md:flex items-center gap-3 px-5 py-3 text-[11px] font-bold uppercase tracking-wide text-slate-400 border-b border-slate-100">
            <div className="w-48">Kandidát</div>
            <div className="flex-1">Pozícia</div>
            <div className="w-40">Výberové konanie</div>
            <div className="w-36">GDPR</div>
            <div className="w-28 text-right">Platný do</div>
          </div>
          {data.candidates.map((c) => (
            <button key={c.id} data-testid={`gdpr-row-${c.id}`} onClick={() => nav(`/company/candidate/${c.id}`)}
              className="w-full flex flex-col md:flex-row md:items-center gap-2 md:gap-3 px-5 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors text-left">
              <div className="w-full md:w-48 font-semibold text-slate-800 truncate">{c.name || "—"}</div>
              {c.shredded ? (
                <>
                  <div className="flex-1 text-sm text-slate-500 truncate" data-testid={`gdpr-shredded-position-${c.id}`}>{(c.positions || []).join(", ") || (c.areas || []).join(", ") || "—"}</div>
                  <div className="w-40 text-xs font-semibold text-red-600">Zamietnutý</div>
                  <div className="w-36 text-xs text-slate-500">Career Score: <strong>{typeof c.career_score === "number" ? c.career_score : "—"}</strong></div>
                  <div className="w-28 md:text-right text-xs text-slate-500">{fmt(c.created_at)}</div>
                </>
              ) : (
                <>
                  <div className="flex-1 text-sm text-slate-500 truncate">{c.title || (c.areas || []).join(", ") || "—"}</div>
                  <div className="w-40 text-xs text-slate-600">{PIPELINE[c.pipeline_status] || c.pipeline_status}</div>
                  <div className="w-36"><GdprBadge status={c.gdpr_status} /></div>
                  <div className="w-28 md:text-right text-xs text-slate-500">{fmt(c.expires_at)}</div>
                </>
              )}
            </button>
          ))}
        </div>
      )}
    </CompanyShell>
  );
}

const TPL_LABELS = {
  consent_request_subject: "Predmet žiadosti o súhlas",
  ack: "Auto-odpoveď: potvrdenie prijatia",
  rejection_no_retention: "Auto-odpoveď: zamietnutie bez uchovania",
  rejection_with_retention: "Auto-odpoveď: zamietnutie s uchovaním",
};

function GdprSettingsPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/company/gdpr/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <div className="mb-6 rounded-3xl bg-white border border-slate-200 p-6 flex justify-center"><Loader2 className="animate-spin text-blue-700" /></div>;

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setTpl = (k, v) => setS((p) => ({ ...p, templates: { ...p.templates, [k]: v } }));
  const setCat = (i, v) => setS((p) => { const c = [...p.source_categories]; c[i] = { ...c[i], label: v }; return { ...p, source_categories: c }; });
  const addCat = () => setS((p) => ({ ...p, source_categories: [...p.source_categories, { key: `custom_${Date.now()}`, label: "" }] }));
  const delCat = (i) => setS((p) => ({ ...p, source_categories: p.source_categories.filter((_, x) => x !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/company/gdpr/settings", {
        retention_days: Number(s.retention_days), expiry_warning_months: Number(s.expiry_warning_months),
        anonymize_rejected: !!s.anonymize_rejected, consent_text: s.consent_text,
        source_categories: s.source_categories.filter((c) => (c.label || "").trim()),
        templates: s.templates,
      });
      setS(data); toast.success("GDPR nastavenia uložené");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const Field = ({ label, children }) => (
    <label className="block"><span className="text-sm font-semibold text-slate-700">{label}</span>{children}</label>
  );

  return (
    <div className="mb-6 rounded-3xl bg-white border border-slate-200 shadow-sm p-6 space-y-5" data-testid="gdpr-settings-panel">
      <h2 className="font-head text-lg font-bold">Nastavenia GDPR</h2>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Doba uchovania (dni)">
          <input type="number" data-testid="gdpr-retention-days" value={s.retention_days} onChange={(e) => set("retention_days", e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-blue-600" />
        </Field>
        <Field label="Upozornenie pred expiráciou (mesiace)">
          <input type="number" data-testid="gdpr-warning-months" value={s.expiry_warning_months} onChange={(e) => set("expiry_warning_months", e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-blue-600" />
        </Field>
        <label className="flex items-end gap-2 pb-1">
          <input type="checkbox" data-testid="gdpr-anon-toggle" checked={!!s.anonymize_rejected} onChange={(e) => set("anonymize_rejected", e.target.checked)} className="h-5 w-5 accent-blue-700" />
          <span className="text-sm font-semibold text-slate-700">Anonymizovať zamietnutých</span>
        </label>
      </div>

      <Field label="Text súhlasu (použite {company} pre názov firmy)">
        <textarea data-testid="gdpr-consent-text" value={s.consent_text} onChange={(e) => set("consent_text", e.target.value)} rows={5}
          className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-600" />
      </Field>
      <p className="text-xs text-slate-400 -mt-3">Právne znenie pred produkčným použitím overte s DPO/právnikom.</p>

      <div>
        <span className="text-sm font-semibold text-slate-700">Zdrojové kategórie</span>
        <div className="mt-2 space-y-2" data-testid="gdpr-source-categories">
          {s.source_categories.map((c, i) => (
            <div key={c.key} className="flex items-center gap-2">
              <input data-testid={`gdpr-cat-${i}`} value={c.label} onChange={(e) => setCat(i, e.target.value)}
                className="flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-blue-600" />
              <button data-testid={`gdpr-cat-del-${i}`} onClick={() => delCat(i)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
            </div>
          ))}
          <button data-testid="gdpr-cat-add" onClick={addCat} className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:underline"><Plus size={14} /> Pridať kategóriu</button>
        </div>
      </div>

      <div className="space-y-3">
        <span className="text-sm font-semibold text-slate-700">Emailové šablóny</span>
        {Object.keys(TPL_LABELS).map((k) => (
          <Field key={k} label={TPL_LABELS[k]}>
            <textarea data-testid={`gdpr-tpl-${k}`} value={(s.templates || {})[k] || ""} onChange={(e) => setTpl(k, e.target.value)}
              rows={k === "consent_request_subject" ? 1 : 3}
              className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-600" />
          </Field>
        ))}
      </div>

      <button data-testid="gdpr-settings-save" onClick={save} disabled={saving}
        className="inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-8 py-3 font-semibold transition-colors active:scale-95 disabled:opacity-60">
        {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Uložiť nastavenia
      </button>
    </div>
  );
}

